/**
 * Client-side pre-validation for blog image uploads.
 *
 * The server is the authority — `POST /blog/images` sniffs magic bytes,
 * rejects SVG by both declared type and content, caps at 5 MB, runs ClamAV
 * fail-closed and re-encodes to strip EXIF. None of that moves here. What
 * moves here is the *refusal*, so a 6 MB phone photo is turned away in front
 * of the author instead of after a slow upload, and so the file picker offers
 * the right three types in the first place.
 *
 * The rules are therefore copied from `app/api/v1/blog/images.py` deliberately
 * and must stay in step with it. Where they could drift, this module is the
 * more permissive of the two by construction: it never *accepts* something the
 * server would reject on grounds it can check (it sniffs the same three magic
 * signatures), and it never blocks something the server would take.
 *
 * SVG gets its own rejection code rather than falling into "unsupported type"
 * because it needs its own sentence: it is not a format we have merely failed
 * to support, it is a script-carrying document being kept off a public
 * marketing site.
 */

/** 5 MB, as `MAX_IMAGE_BYTES` in app/api/v1/blog/images.py. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type AcceptedImageType = (typeof ACCEPTED_IMAGE_TYPES)[number];

/** `accept` attribute for the file input — the same allow-list. */
export const IMAGE_ACCEPT_ATTR = ACCEPTED_IMAGE_TYPES.join(',');

export type ImageRejectionCode = 'empty' | 'too_large' | 'svg' | 'unsupported_type';

export type ImageCheck =
  | { ok: true; contentType: AcceptedImageType | null }
  | { ok: false; code: ImageRejectionCode; message: string };

const OK_UNSNIFFED: ImageCheck = { ok: true, contentType: null };

function reject(code: ImageRejectionCode, message: string): ImageCheck {
  return { ok: false, code, message };
}

const SVG_MESSAGE =
  'SVG files are not accepted. They can carry script and these images are ' +
  'published on the public site. Upload a JPEG, PNG or WebP.';

const UNSUPPORTED_MESSAGE = 'Only JPEG, PNG and WebP images are accepted.';

function tooLargeMessage(size: number): string {
  const mb = size / (1024 * 1024);
  // One decimal so a 5.2 MB file does not read as "5 MB, maximum is 5 MB".
  return `That image is ${mb.toFixed(1)} MB. The maximum is 5 MB.`;
}

/** Extension, lowercased, without the dot. '' when the name carries none. */
function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

export interface ImageFileMeta {
  name?: string;
  size: number;
  /** The browser's guess. Never trusted on its own — the server re-sniffs. */
  type?: string;
}

/**
 * Metadata-only check: size, declared type, extension. Runs synchronously the
 * instant a file is picked.
 *
 * A file whose type the browser could not guess (`type: ''`, common for
 * drag-and-drop from some file managers) passes this check unless the *name*
 * says SVG — the byte sniff below, and ultimately the server, decides.
 */
export function checkImageMeta(meta: ImageFileMeta): ImageCheck {
  const declared = (meta.type ?? '').toLowerCase();
  const ext = extensionOf(meta.name ?? '');

  // SVG first: it must be named as itself whichever way it was declared.
  if (declared.includes('svg') || ext === 'svg' || ext === 'svgz') {
    return reject('svg', SVG_MESSAGE);
  }
  if (meta.size <= 0) {
    return reject('empty', 'That file is empty.');
  }
  if (meta.size > MAX_IMAGE_BYTES) {
    return reject('too_large', tooLargeMessage(meta.size));
  }
  if (declared && !(ACCEPTED_IMAGE_TYPES as readonly string[]).includes(declared)) {
    return reject('unsupported_type', UNSUPPORTED_MESSAGE);
  }
  return OK_UNSNIFFED;
}

/**
 * The three magic signatures the server accepts, and nothing else.
 * Mirrors `images.py::_sniff_image`.
 */
export function sniffImageType(bytes: Uint8Array): AcceptedImageType | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  const PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.length >= 8 && PNG.every((b, i) => bytes[i] === b)) {
    return 'image/png';
  }
  // RIFF....WEBP
  if (
    bytes.length >= 12 &&
    String.fromCharCode(...Array.from(bytes.slice(0, 4))) === 'RIFF' &&
    String.fromCharCode(...Array.from(bytes.slice(8, 12))) === 'WEBP'
  ) {
    return 'image/webp';
  }
  return null;
}

/** Mirrors `images.py::_looks_like_svg` — declared type, or an XML/SVG head. */
export function looksLikeSvg(bytes: Uint8Array, declaredType?: string | null): boolean {
  if (declaredType && declaredType.toLowerCase().includes('svg')) return true;
  const head = String.fromCharCode(...Array.from(bytes.slice(0, 512))).trimStart();
  return head.startsWith('<?xml') || head.startsWith('<svg') || head.includes('<svg');
}

/** Byte-level check, run on the head of the file once it has been read. */
export function checkImageBytes(bytes: Uint8Array, declaredType?: string | null): ImageCheck {
  if (looksLikeSvg(bytes, declaredType ?? null)) {
    return reject('svg', SVG_MESSAGE);
  }
  const sniffed = sniffImageType(bytes);
  if (sniffed === null) {
    return reject(
      'unsupported_type',
      `${UNSUPPORTED_MESSAGE} The file's actual contents are checked, not its name.`,
    );
  }
  return { ok: true, contentType: sniffed };
}

/**
 * Full pre-flight for a picked file: metadata, then the first 512 bytes.
 *
 * Reads only the head — a 5 MB file is never pulled into memory to be told it
 * is a PNG.
 */
export async function validateImageFile(file: File): Promise<ImageCheck> {
  const meta = checkImageMeta({ name: file.name, size: file.size, type: file.type });
  if (!meta.ok) return meta;
  const head = new Uint8Array(await file.slice(0, 512).arrayBuffer());
  return checkImageBytes(head, file.type);
}
